<template>
  <div
    v-if="!isCTA"
    class="books__item"
    :class="[doc.id, { 'books__item--20321B': showNewSaveDesign }]"
  >
    <template v-if="showNewSaveDesign && view.hasUserData(doc)">
      <SaveButton class="books__save-button" :doc="doc" :docsView="view" />
      <DocMoreMenu
        class="books__more-menu"
        :class="{ 'books__more-menu--above-progress': doc.user?.progress }"
        :doc="doc"
        :docsView="view"
      />
    </template>
    <router-link :to="view.startLinkParams(doc)" class="books__link">
      <DocTypeBadge v-if="showNewSaveDesign" docType="book" />
      <div v-else class="books__icons">
        <BookListenIcon v-if="doc.has_audio" />
        <FreeBookIcon v-if="doc.is_free && showFreeBooks" />
      </div>
      <BookCardThumbnail
        class="books__img-box"
        coverClass="books__img"
        fallbackCoverClass="books__img"
        :doc="doc"
        :cacheBust="true"
      />

      <DocProgressBar class="books__progress" :progress="doc.user?.progress" />
      <div class="books__text-box">
        <div class="books__text">
          <div class="books__upper-box">
            <div v-if="!showNewSaveDesign" class="books__wrapper">
              <h5 class="books__category card__category">
                {{ view.firstTag(doc) }}
              </h5>
              <DocListsMenu
                v-if="view.hasUserData(doc) && doc.user.custom_lists"
                :doc="doc"
                :docsView="view"
              />
            </div>
            <h3 class="books__title card__title">{{ doc.title }}</h3>
            <h4 class="books__author card__author">
              {{ showNewSaveDesign ? doc.author : `By ${doc.author}` }}
            </h4>
          </div>
          <div class="books__lower-box">
            <div class="books__info">
              <DocBadges
                v-if="showNewSaveDesign"
                :doc="doc"
                :free="doc.is_free && showFreeBooks"
              />
              <TimeEstimate v-else :doc="doc" />
            </div>
            <DocProgressBar
              v-if="doc.user?.progress"
              class="books__progress--mobile"
              :class="{
                'books__progress--rightmenu':
                  !showNewSaveDesign && view.showActionsButton(doc),
              }"
              :progress="doc.user.progress"
            />
          </div>
        </div>
        <template v-if="!showNewSaveDesign">
          <CustomListDocMenu
            v-if="
              view.showActionsButton(doc) &&
              customList?.doc_type === 'custom_list'
            "
            class="books__actions books__actions--list-page"
            :doc="doc"
            :docsView="view"
            :list="customList"
            position="right"
          />
          <DocLibraryMenu
            v-else-if="view.showActionsButton(doc)"
            class="books__actions books__actions--library"
            :doc="doc"
            :docsView="view"
            position="right"
          />
        </template>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import DocBadges from '@/app/components/doc/DocBadges.vue'
import DocListsMenu from '@/app/components/doc/DocListsMenu.vue'
import DocMoreMenu from '@/app/components/doc/DocMoreMenu.vue'
import DocProgressBar from '@/app/components/doc/DocProgressBar.vue'
import DocTypeBadge from '@/app/components/doc/DocTypeBadge.vue'
import SaveButton from '@/app/components/doc/SaveButton.vue'
import TimeEstimate from '@/app/components/doc/TimeEstimate.vue'
import BookListenIcon from '@/app/components/book/card/BookListenIcon.vue'
import FreeBookIcon from '@/app/components/book/card/FreeBookIcon.vue'
import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import DocLibraryMenu from '@/app/components/doc/DocLibraryMenu.vue'
import CustomListDocMenu from '@/app/components/custom.lists/CustomListDocMenu.vue'
import { saveForLater20321Treatment } from '@/services/ab/experiments'

import type { BookListItem, CustomList } from '@/models/interfaces'
import type { BooksView } from '@/models/doc.book'

const props = withDefaults(
  defineProps<{
    doc: BookListItem
    view: BooksView
    showFreeBooks?: boolean
    customList?: CustomList
  }>(),
  {
    showFreeBooks: true,
  },
)

const showNewSaveDesign = saveForLater20321Treatment()

const isCTA = computed(() => {
  return props.doc.title === 'CTA'
})
</script>

<style lang="scss" scoped>
.books {
  &__actions {
    position: absolute;
    right: 16px;
    bottom: 16px;
    width: fit-content;
  }

  &__save-button {
    position: absolute;
    z-index: 2;
    top: 12px;
    right: 12px;
  }

  &__more-menu {
    position: absolute;
    z-index: 2;
    right: 20px;
    bottom: 18px;

    @media (max-width: $w-tablet-s) {
      // Line up with the Save button.
      right: 12px;
      bottom: 17px;
    }

    // On mobile, show the menu button above the progress bar.
    &--above-progress {
      @media (max-width: $w-tablet-s) {
        bottom: 30px;
      }
    }

    // Listing blocks don't show the mobile progress bar.
    .listing-block &--above-progress {
      @media (max-width: $w-tablet-s) {
        bottom: 17px;
      }
    }
  }

  &__item--20321B {
    .books__img-box {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      height: 200px;

      :deep(.books__img) {
        --cover-width: 90px;
      }

      @media (max-width: $w-tablet-s) {
        height: auto;
      }
    }

    .books__text-box {
      min-height: 149px;
      padding: 16px 20px 20px;

      @media (max-width: $w-tablet-s) {
        min-height: auto;
        padding: 23px 19px 19px 16px;
      }
    }

    .books__upper-box {
      @media (max-width: $w-tablet-s) {
        padding-right: 32px;
      }
    }

    .books__title {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 22px;
    }

    .books__author {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      line-height: 21px;
      margin-bottom: 0;
    }
  }
}
</style>
